/**
 * Staged Hono data plane — used when VITE_API_BASE_URL is set.
 */
import { apiFetch, resolveApiBaseUrl } from './apiClient'

const TOKEN_KEY = 'hcp_access_token'

export function getHonoAccessToken(): string | null {
  if (typeof window === 'undefined' || !window.localStorage) return null
  const t = window.localStorage.getItem(TOKEN_KEY)
  return t && t.trim() ? t : null
}

export function setHonoAccessToken(token: string | null): void {
  if (typeof window === 'undefined' || !window.localStorage) return
  if (!token) window.localStorage.removeItem(TOKEN_KEY)
  else window.localStorage.setItem(TOKEN_KEY, token)
}

export const honoAuth = {
  async getSession() {
    const access_token = getHonoAccessToken()
    if (!access_token) return { data: { session: null }, error: null }
    return {
      data: {
        session: {
          access_token,
          user: { id: 'hono-session', email: undefined as string | undefined },
        },
      },
      error: null,
    }
  },
  async getUser() {
    const access_token = getHonoAccessToken()
    if (!access_token) return { data: { user: null }, error: null }
    return { data: { user: { id: 'hono-session' } }, error: null }
  },
  async signOut() {
    setHonoAccessToken(null)
    return { error: null }
  },
  onAuthStateChange(cb: (event: string, session: unknown) => void) {
    void cb
    return { data: { subscription: { unsubscribe() {} } } }
  },
  async signInWithPassword(creds: { email: string; password: string }) {
    void creds
    return {
      data: { session: null, user: null },
      error: { message: 'Use Hono /auth endpoints — password sign-in wired in Better Auth phase' },
    }
  },
}

export const honoStorage = {
  from(bucket: string) {
    return {
      async upload(path: string, file: Blob, opts?: unknown) {
        void file
        void opts
        const token = getHonoAccessToken()
        const res = await apiFetch('/functions/v1/evidence', {
          method: 'POST',
          accessToken: token,
          body: JSON.stringify({ action: 'upload_intent', bucket, path }),
        })
        if (!res.ok) return { data: null, error: { message: `upload_intent failed ${res.status}` } }
        const data = await res.json().catch(() => ({}))
        return { data, error: null }
      },
      async createSignedUrl(path: string, expiresIn: number) {
        void expiresIn
        const token = getHonoAccessToken()
        const res = await apiFetch('/functions/v1/evidence', {
          method: 'POST',
          accessToken: token,
          body: JSON.stringify({ action: 'signed_url', path }),
        })
        if (!res.ok) return { data: null, error: { message: `signed_url failed ${res.status}` } }
        const data = (await res.json().catch(() => ({}))) as { signedUrl?: string }
        return {
          data: { signedUrl: data.signedUrl ?? `${resolveApiBaseUrl()}/storage/${path}` },
          error: null,
        }
      },
    }
  },
}

export const honoRealtime = {
  channel(name: string) {
    const listeners: Array<(payload: unknown) => void> = []
    let es: EventSource | null = null
    const channel = {
      on(event: string, cb: (payload: unknown) => void) {
        void event
        listeners.push(cb)
        return channel
      },
      subscribe(cb?: (status: string) => void) {
        const base = resolveApiBaseUrl()
        const url = `${base}/sse/${encodeURIComponent(name)}`
        try {
          es = new EventSource(url)
          es.onmessage = (ev) => {
            let payload: unknown = ev.data
            try {
              payload = JSON.parse(ev.data)
            } catch {
              /* raw */
            }
            for (const l of listeners) l(payload)
          }
          es.onopen = () => cb?.('SUBSCRIBED')
          es.onerror = () => cb?.('CHANNEL_ERROR')
        } catch {
          cb?.('CHANNEL_ERROR')
        }
        return channel
      },
      unsubscribe() {
        es?.close()
        es = null
      },
    }
    return channel as unknown as import('@supabase/supabase-js').RealtimeChannel
  },
  removeChannel(ch: { unsubscribe?: () => void }) {
    ch?.unsubscribe?.()
  },
}
